import clsx from 'clsx'
import React from 'react'
import PropTypes from 'prop-types'
import { Line } from 'react-chartjs-2'
import './LineChart.module.scss'
import 'chartjs-plugin-datalabels'
import { lineOptions } from './Constant'

function isDarkMode() {
  if (typeof document === 'undefined') return false
  return document.documentElement.getAttribute('data-bs-theme') === 'dark'
}

// Chart.js 2 draws ticks on canvas and does not resolve `var(--token)` — use computed RGB/hex.
function readCssVar(name, fallback) {
  if (typeof document === 'undefined') return fallback
  const v = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string') return undefined
  const h = hex.replace('#', '').trim()
  if (![3, 6].includes(h.length)) return undefined
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  if ([r, g, b].some((v) => Number.isNaN(v))) return undefined
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Upper bound for Y scale from series max (replaces a fixed `suggestedMax` that squashed low values). */
function computeYSuggestedMax(chartData) {
  const series = chartData?.datasets || []
  let m = 0
  series.forEach((ds) => {
    ;(ds?.data || []).forEach((v) => {
      if (typeof v === 'number' && !Number.isNaN(v)) {
        m = Math.max(m, v)
      }
    })
  })
  if (m <= 0) {
    return 8
  }
  return Math.max(5, Math.ceil(m * 1.15))
}

const propTypes = {
  /**
   * Data containing complete object for line chart.
   */
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
        fill: PropTypes.bool,
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
        pointRadius: PropTypes.number,
      }),
    ),
  }).isRequired,
  /**
   * Heading Label for line chart.
   */
  heading: PropTypes.string,
}

function LineChart(props) {
  const { data, heading = '' } = props ?? {}
  const dark = isDarkMode()

  const ySuggestedMax = React.useMemo(() => computeYSuggestedMax(data), [data])

  const applyYAxisSuggestedMax = React.useCallback(
    (axis) => ({
      ...axis,
      ticks: {
        ...(axis.ticks || {}),
        suggestedMax: ySuggestedMax,
      },
    }),
    [ySuggestedMax],
  )

  // Chart.js 2 draws grid lines on canvas and does not resolve `var(--token)` — read computed theme tokens only (see `--aug-chart-line-*` on `:root`).
  const themedOptions = React.useMemo(() => {
    const tickColor = readCssVar('--aug-chart-line-axis-tick', '') || readCssVar('--bs-secondary-color', '')
    const gridY = readCssVar('--aug-chart-line-grid', '') || readCssVar('--bs-border-color', '')
    const gridX = readCssVar('--aug-chart-line-grid-x', '') || readCssVar('--bs-border-color', '')

    return {
      ...lineOptions,
      scales: {
        ...lineOptions.scales,
        xAxes: (lineOptions.scales?.xAxes || []).map((axis) => ({
          ...axis,
          ticks: { ...(axis.ticks || {}), fontColor: tickColor },
          gridLines: { ...(axis.gridLines || { display: false }), color: gridX },
        })),
        yAxes: (lineOptions.scales?.yAxes || []).map((axis) => {
          const withY = applyYAxisSuggestedMax(axis)
          return {
            ...withY,
            ticks: { ...(withY.ticks || {}), fontColor: tickColor },
            gridLines: {
              ...(withY.gridLines || {}),
              color: gridY,
            },
          }
        }),
      },
    }
  }, [applyYAxisSuggestedMax, dark])

  const chartData = React.useMemo(() => {
    const withStroke = (ds) => ({
      ...ds,
      borderWidth: typeof ds.borderWidth === 'number' ? ds.borderWidth : 2,
    })
    if (!dark) {
      return {
        ...data,
        datasets: (data?.datasets || []).map(withStroke),
      }
    }
    return {
      ...data,
      datasets: (data?.datasets || []).map((ds) => {
        const stroke = withStroke(ds)
        // Prefer the series color for the filled area under the curve.
        // If the caller already supplies a good dark backgroundColor, keep it.
        const fillBg = stroke?.fill && typeof stroke?.backgroundColor === 'string' ? stroke.backgroundColor : undefined

        const computedBg =
          stroke?.fill && stroke?.borderColor
            ? hexToRgba(stroke.borderColor, 0.18) || hexToRgba(stroke.borderColor, 0.22)
            : undefined

        return {
          ...stroke,
          backgroundColor: fillBg || computedBg || stroke.backgroundColor,
        }
      }),
    }
  }, [dark, data])

  return (
    <div className={clsx('Uic-LineChart-base')}>
      <div className={clsx('Uic-LineChart-inner_div')}>
        <h6>{heading}</h6>
        <div className={clsx('Uic-LineChart-labels_div')}>
          {data?.datasets?.map((item) => (
            <div key={item?.label} className={clsx('Uic-LineChart-label_sub_div')}>
              <div className={clsx('Uic-LineChart-dot')} style={{ backgroundColor: item?.borderColor }} />

              <span className={clsx('Uic-LineChart-label_text')}>{item?.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={clsx('Uic-LineChart-chart_div')}>
        <Line className={clsx('Uic-LineChart-chart')} options={themedOptions} data={chartData} />
      </div>
    </div>
  )
}

LineChart.propTypes = propTypes

export default LineChart
